import { defineComponent, reactive, ref, toRefs, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ArrowLeft, Plus, Delete } from '@element-plus/icons-vue';
import { testProcessDetail, testProcessDetailList, addTestProcessDetail, updateTestProcessDetail, deleteTestProcessDetail, getEnabledTestItems } from '@/api/deviceManage';
export default defineComponent({
  name: 'TestProcessDetail',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const formRef = ref(null);
    const processId = route.query.processId || '';
    const state = reactive({
      tableLoading: false,
      saveLoading: false,
      dialogVisible: false,
      title: '新增流程明细',
      isEdit: false,
      multipleSelection: [],
      processInfo: {},
      detailList: [],
      testItemOptions: [],
      form: {
        itemId: '',
        stageNo: 1,
        field: 'bottlePressure',
        threshold: null,
        targetPressure: null,
        cycleCount: null,
        repeatabilityThreshold: null
      },
      formRules: {
        itemId: [{
          required: true,
          message: '请选择测试项',
          trigger: 'change'
        }],
        stageNo: [{
          required: true,
          message: '请输入阶段编号',
          trigger: 'blur'
        }],
        threshold: [{
          required: true,
          message: '请输入保压阈值',
          trigger: 'blur'
        }],
        targetPressure: [{
          required: true,
          message: '请输入目标压力',
          trigger: 'blur'
        }],
        cycleCount: [{
          required: true,
          message: '请输入循环次数',
          trigger: 'blur'
        }],
        repeatabilityThreshold: [{
          required: true,
          message: '请输入重复性阈值',
          trigger: 'blur'
        }]
      }
    });
    const goBack = () => {
      router.back();
    };

    // 加载流程基本信息
    const loadProcessInfo = () => {
      if (!processId) return;
      testProcessDetail(processId).then(res => {
        if (res.data) {
          state.processInfo = res.data;
        }
      }).catch(err => {
        console.error('获取流程详情失败:', err);
      });
    };

    // 加载流程明细列表
    const loadDetailList = () => {
      if (!processId) return;
      state.tableLoading = true;
      testProcessDetailList(processId).then(res => {
        state.detailList = res.data || [];
        state.tableLoading = false;
      }).catch(err => {
        console.error('获取流程明细失败:', err);
        state.tableLoading = false;
      });
    };

    // 加载测试项下拉选项
    const loadTestItems = () => {
      getEnabledTestItems().then(res => {
        state.testItemOptions = res.data || [];
      }).catch(err => {
        console.error('获取测试项列表失败:', err);
      });
    };

    // 表格多选变化
    const handleSelectionChange = val => {
      state.multipleSelection = val;
    };

    // 新增明细
    const handleAdd = () => {
      state.form = {
        itemId: '',
        stageNo: 1,
        field: 'bottlePressure',
        threshold: null,
        targetPressure: null,
        cycleCount: null,
        repeatabilityThreshold: null
      };
      state.isEdit = false;
      state.dialogVisible = true;
      state.title = '新增流程明细';
    };

    // 修改明细
    const handleEdit = row => {
      state.form = {
        id: row.id,
        itemId: row.itemId,
        stageNo: row.stageNo,
        field: 'bottlePressure',
        threshold: row.testConfig?.metric?.threshold ?? null,
        targetPressure: row.testConfig?.metric?.targetPressure ?? null,
        cycleCount: row.testConfig?.cycleCount ?? null,
        repeatabilityThreshold: row.testConfig?.repeatabilityThreshold ?? null
      };
      state.isEdit = true;
      state.dialogVisible = true;
      state.title = '修改流程明细';
    };

    // 关闭弹窗重置表单
    const closeDialog = () => {
      state.form = {
        itemId: '',
        stageNo: 1,
        field: 'bottlePressure',
        threshold: null,
        targetPressure: null,
        cycleCount: null,
        repeatabilityThreshold: null
      };
      state.isEdit = false;
    };

    // 提交表单
    const submitForm = () => {
      formRef.value.validate(valid => {
        if (valid) {
          state.saveLoading = true;
          const submitData = {
            ...state.form
          };
          const requestFn = state.isEdit ? updateTestProcessDetail(submitData.id, submitData) : addTestProcessDetail(processId, submitData);
          requestFn.then(res => {
            if (res.code == 0) {
              ElMessage.success(state.isEdit ? '修改成功' : '新增成功');
              state.dialogVisible = false;
              state.saveLoading = false;
              loadDetailList();
            }
          }).catch(() => {
            state.saveLoading = false;
          });
        }
      });
    };

    // 删除单条明细
    const handleDel = row => {
      ElMessageBox.confirm('确定是否删除该明细?', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteTestProcessDetail(row.id).then(res => {
          if (res.code == 0) {
            ElMessage.success('删除成功');
            loadDetailList();
          } else {
            ElMessage.warning(res.message);
          }
        });
      }).catch(() => {});
    };

    // 批量删除
    const handleBatchDel = () => {
      if (!state.multipleSelection.length) {
        ElMessage.warning('请至少选择一条数据删除');
        return;
      }
      const ids = state.multipleSelection.map(item => item.id);
      ElMessageBox.confirm(`确定删除选中的 ${ids.length} 条明细?`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const promises = ids.map(id => deleteTestProcessDetail(id));
        Promise.all(promises).then(() => {
          ElMessage.success('批量删除成功');
          loadDetailList();
        }).catch(() => {
          ElMessage.warning('部分删除失败');
          loadDetailList();
        });
      }).catch(() => {});
    };
    onMounted(() => {
      loadProcessInfo();
      loadDetailList();
      loadTestItems();
    });
    return {
      ...toRefs(state),
      formRef,
      ArrowLeft,
      Plus,
      Delete,
      goBack,
      handleAdd,
      handleEdit,
      closeDialog,
      submitForm,
      handleDel,
      handleBatchDel,
      handleSelectionChange
    };
  }
});